import React, { useEffect, useState } from 'react';
import '../assets/styles/App.css';
import { Navbar, Container, Dropdown } from 'react-bootstrap';
import { Outlet, useNavigate } from "react-router-dom";
import { getBackgroundURL, getLogoURL, logout } from "../actions/authActions";
import { connect } from "react-redux";
import { IUser } from "../models/IUser";
import ReduxToastr from "react-redux-toastr";
import { Home, User, Cog } from "lucide-react";
import { isString } from "lodash";
import cookie from "../utilities/cookie";
import Flag from 'react-world-flags';
import { Constants } from "../utilities/constants";

import { useTranslation } from "react-i18next";
import { ReactComponent as Hamburger } from "../assets/images/hamburger.svg";
import ApiClient from "../utilities/http";
import { Endpoints } from "../utilities/endpoints";

interface Props {
    dispatch: Function;
    user: IUser;
    isAuthenticated: boolean;
    passwordChangeRequired?: boolean;
    authErrorCode?: string;
    logoUrl?: string,
    backgroundURL?: string,
}
interface LogoProps {
    logoUrl?: string;
}

interface EmergencyContact {
    text: string;
    contact: string;
    href: string;
}

interface MobileAppInfoResponse {
    emergency_contact?: EmergencyContact | null;
}

const isEmergencyContact = (value: unknown): value is EmergencyContact => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const contact = value as Partial<EmergencyContact>;

    return Boolean(contact.text && contact.contact && contact.href);
};

const Protected: React.FC<Props> = ({ dispatch, logoUrl, backgroundURL, isAuthenticated, passwordChangeRequired, authErrorCode }) => {
    const [emergencyContact, setEmergencyContact] = useState<EmergencyContact | null>(null);
    const { i18n } = useTranslation();
    const [language, setLanguage] = useState<string>(i18n.language); // default language: English

    const handleLogout = () => dispatch(logout());
    const navigate = useNavigate();
    const menuItems = [
        { name: 'Home', icon: Home, target: '/dashboard' },
        { name: 'Settings', icon: Cog, target: '/settings' },
        { name: 'Logout', icon: User, target: handleLogout },
    ];
    const handleMenuClick = (target: string | Function) => {
        if (isString(target)) {
            navigate(target);
            return;
        }
        target();
    }

    // @ts-ignore
    const trans = (txt: string) => i18n.t(txt)

    useEffect(() => {
        if (passwordChangeRequired || authErrorCode === Constants.PASSWORD_CHANGE_REQUIRED) {
            navigate('/password-change-required');
            return;
        }

        if (isAuthenticated) {
            cookie.remove(Constants.AUTH_ERROR_CODE_COOKIE);
            cookie.remove(Constants.PENDING_LOGIN_COOKIE);
            cookie.remove(Constants.PENDING_PLATFORM_COOKIE);
            return;
        }

        if (cookie.get(Constants.AUTH_ERROR_CODE_COOKIE) === Constants.PASSWORD_CHANGE_REQUIRED) {
            navigate('/password-change-required');
            return;
        }

        if (!isAuthenticated) {
            navigate('/login');
        }
    }, [authErrorCode, isAuthenticated, navigate, passwordChangeRequired]);


    if (backgroundURL) {
        cookie.put('background_url', backgroundURL)
    }

    if (logoUrl) {
        cookie.put('logo_url', logoUrl)
    }

    useEffect(() => {
        dispatch(getLogoURL())
        dispatch(getBackgroundURL())
        ApiClient.get<MobileAppInfoResponse>(Endpoints.MOBILE_APP_INFO as string).then((response) => {
            const emergencyContact = response.data?.emergency_contact;

            setEmergencyContact(isEmergencyContact(emergencyContact) ? emergencyContact : null);
        }).catch(() => {
            setEmergencyContact(null);
        });
    }, [dispatch])

    const handleLanguageChange = (lang: string) => {
        setLanguage(lang);
        i18n.changeLanguage(lang); // Change language in i18n
    };

    const Logo: React.FC<LogoProps> = ({ logoUrl }) => {
        const src = logoUrl || cookie.get('logo_url')
        return (<img src={src} alt="Platform Logo" style={{ maxWidth: '150px', maxHeight: '30px' }} />)
    };

    const MemoizedLogo = React.memo(Logo);

    return (
        <div className="dashboard-app d-flex flex-column min-vh-100">
            <Navbar bg="primary" variant="dark" className="shadow-md p-1">
                <Container fluid className="d-flex justify-content-between align-items-center">
                    <Navbar.Brand className="fw-bold" onClick={() => navigate('/dashboard')}>
                        <MemoizedLogo logoUrl={logoUrl} />
                    </Navbar.Brand>
                    <div className="d-flex align-items-center">
                        {/* Language Button */}
                        <Dropdown align="end" className="me-2">
                            <Dropdown.Toggle variant="link" className="p-1 text-white" id="language-dropdown">
                                <Flag code={language === 'en' ? 'GB' : language.toUpperCase()} alt="language" className="me-2" style={{ width: '20px' }} />
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => handleLanguageChange('nl')}>
                                    <Flag code="NL" alt="Dutch" style={{ width: '20px' }} /> {trans('dutch')}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleLanguageChange('en')}>
                                    <Flag code="GB" alt="English" style={{ width: '20px' }} /> {trans('english')}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleLanguageChange('de')}>
                                    <Flag code="DE" alt="German" style={{ width: '20px' }} /> {trans('german')}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                        {/* Bell Icon */}
                        {/* <Button variant="link" className="p-1 text-white" onClick={handleQrCodeClick}> */}
                        {/* <Bell size={24} style={{ color: 'white' }} /> */}
                        {/* </Button> */}

                        {/* Menu Icon */}
                        {/* <Button variant="link" className="p-1 text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                            <Menu size={24} />
                        </Button> */}
                        {/* Add a dropdown? */}
                        <Dropdown >
                            <Dropdown.Toggle>
                                <Hamburger style={{ color: 'white' }} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu align='end'>
                                {menuItems.map((item) => (
                                    <Dropdown.Item key={item.name}>
                                        <div onClick={() => handleMenuClick(item.target)}>
                                            <item.icon />&nbsp;{trans(item.name)}
                                        </div>
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </Container>
            </Navbar>

            {/* Body Content */}
            <main className="d-flex flex-column flex-grow-1 bg-light position-relative p-2">
                <Outlet></Outlet>
            </main>

            <ReduxToastr
                timeOut={7000}
                newestOnTop={false}
                preventDuplicates
                position="top-right"
                transitionIn="fadeIn"
                transitionOut="fadeOut"
                closeOnToastrClick />
            {/* Footer */}
            <footer className="mobile-app-footer bg-light py-3 mt-auto shadow-inner text-center">
                {emergencyContact && (
                    <div className="mobile-app-footer-emergency">
                        <span>{emergencyContact.text}</span>{' '}
                        <a href={emergencyContact.href} aria-label={`${emergencyContact.text} ${emergencyContact.contact}`}>
                            {emergencyContact.contact}
                        </a>
                    </div>
                )}
                <small>&copy; 2026 Powered by AllTrace</small>
            </footer>
        </div>

    );
}

const mapStateToProps = (state: any) => ({
    isAuthenticated: state.auth.isAuthenticated,
    passwordChangeRequired: state.auth.passwordChangeRequired,
    authErrorCode: state.auth.authErrorCode,
    user: state.auth.currentUser,
    logoUrl: state.setting.logoURL,
    backgroundURL: state.setting.backgroundURL
});

export default connect(mapStateToProps)(Protected);
